import React from 'react';
import { format, parse, isValid, differenceInCalendarDays } from 'date-fns';
import { Calendar as CalendarIcon, ArrowRight } from 'lucide-react';
import { IMaskInput } from 'react-imask';
import IMask from 'imask';
import { FieldError, FieldCard, tintFromAccent } from './_shared';
import DateRangePopover, { PresetChip, type DateRangePreset } from './DateRangePopover';

// CTV-558: typeable MM/DD/YYYY inputs backed by imask. Slashes are mask
// literals (not editable), digits are bounded per component (MM 1–12,
// DD 1–31, YYYY currentYear-1..currentYear+5), and out-of-range chars
// are rejected at type-time. Commit-on-blur with revert-on-invalid
// keeps the field value clean.

interface DateRangeFieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  startValue: string;
  endValue: string;
  onChange: (next: { start: string; end: string }) => void;
  min?: string;
  minDisabledMessage?: string;
  startPresets?: DateRangePreset[];
  endPresets?: DateRangePreset[];
  accentColor: string;
  primaryColor?: string;
}

const DISPLAY_FORMAT = 'MM/dd/yyyy';

const formatForDisplay = (ymd: string): string => {
  if (!ymd) return '';
  const d = parse(ymd, 'yyyy-MM-dd', new Date());
  return isValid(d) ? format(d, DISPLAY_FORMAT) : '';
};

// Strict MM/DD/YYYY parser used at commit-time. Round-trips through
// date-fns format so impossible calendar dates that silently roll over
// (e.g. 02/30/2026 → 03/02/2026) are rejected. The imask Date mask
// blocks most invalid inputs at type-time, but date-fns parse is lenient
// so the round-trip check is the second line of defence.
const parseStrictDate = (text: string): Date | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return null;
  const parsed = parse(trimmed, 'MM/dd/yyyy', new Date());
  if (!isValid(parsed)) return null;
  if (format(parsed, 'MM/dd/yyyy') !== trimmed) return null;
  return parsed;
};

// Composite date mask config used for both inputs.
// - Year window starts at the *current* year so imask's autofix for
//   partial year inputs lands on the current year (2026, not the
//   previous year's 2025) and goes 5 years forward.
// - `lazy: true` shows the native HTML placeholder ("MM/DD/YYYY") in
//   slate-300 when the input is empty, rather than the imask
//   underscored scaffolding ("__/__/20__") which reads as broken.
const buildDateMaskConfig = () => {
  const currentYear = new Date().getFullYear();
  return {
    mask: Date,
    pattern: 'MM/`DD/`YYYY',
    blocks: {
      MM: { mask: IMask.MaskedRange, from: 1, to: 12, maxLength: 2 },
      DD: { mask: IMask.MaskedRange, from: 1, to: 31, maxLength: 2 },
      YYYY: { mask: IMask.MaskedRange, from: currentYear, to: currentYear + 5, maxLength: 4 },
    },
    // imask types `format`/`parse` to accept the union DateValue (=
    // Date | null). Null is only fed in when the mask is empty, which
    // we want to render as the placeholder ('' triggers it), so we
    // short-circuit.
    format: (d: Date | null) => (d ? format(d, 'MM/dd/yyyy') : ''),
    parse: (s: string) => parse(s, 'MM/dd/yyyy', new Date()),
    lazy: true,
    overwrite: true as const,
    autofix: 'pad' as const,
  };
};

const DateRangeField: React.FC<DateRangeFieldProps> = ({
  id, label, required, error, startValue, endValue, onChange, min,
  minDisabledMessage, startPresets, endPresets, accentColor, primaryColor,
}) => {
  const [open, setOpen] = React.useState(false);
  const triggerRowRef = React.useRef<HTMLDivElement | null>(null);

  // Per-input draft text. Synced from props when the field isn't focused.
  // While focused, the local draft wins so a calendar-driven onChange
  // can't clobber an in-flight typed value.
  const [startText, setStartText] = React.useState(() => formatForDisplay(startValue));
  const [endText, setEndText] = React.useState(() => formatForDisplay(endValue));
  const [startFocused, setStartFocused] = React.useState(false);
  const [endFocused, setEndFocused] = React.useState(false);

  React.useEffect(() => {
    if (!startFocused) setStartText(formatForDisplay(startValue));
  }, [startValue, startFocused]);
  React.useEffect(() => {
    if (!endFocused) setEndText(formatForDisplay(endValue));
  }, [endValue, endFocused]);

  const start = startValue ? parse(startValue, 'yyyy-MM-dd', new Date()) : null;
  const end = endValue ? parse(endValue, 'yyyy-MM-dd', new Date()) : null;
  const hasStart = start && isValid(start);
  const hasEnd = end && isValid(end);
  const durationDays = hasStart && hasEnd
    ? differenceInCalendarDays(end!, start!) + 1
    : null;

  const applyStartPreset = (next: { start?: string; end?: string }) => {
    const nextStart = next.start ?? startValue;
    const nextEnd = endValue;
    if (nextStart && nextEnd) onChange({ start: nextStart, end: nextEnd });
    else if (nextStart && !nextEnd) onChange({ start: nextStart, end: '' });
  };
  const applyEndPreset = (next: { start?: string; end?: string }) => {
    const nextStart = startValue;
    const nextEnd = next.end ?? endValue;
    if (nextStart && nextEnd) onChange({ start: nextStart, end: nextEnd });
    else if (nextEnd && !nextStart) onChange({ start: '', end: nextEnd });
  };

  // Commit on blur/Enter. Empty string clears that endpoint; anything
  // else has to clear three checks before it lands in form state:
  //   1. Strict MM/DD/YYYY parse (rejects rollovers like 02/30).
  //   2. Date is on or after `min` (earliest start = today + 3).
  //   3. Order is preserved: start strictly before end.
  // Any failure silently reverts the display to the last committed
  // value — the revert IS the feedback.
  const minDate = React.useMemo(
    () => (min ? parse(min, 'yyyy-MM-dd', new Date()) : null),
    [min],
  );

  const commitStart = (text: string) => {
    const revert = () => setStartText(formatForDisplay(startValue));

    if (!text.trim()) {
      if (startValue !== '') onChange({ start: '', end: endValue });
      return;
    }
    const parsed = parseStrictDate(text);
    if (!parsed) { revert(); return; }
    if (minDate && parsed < minDate) { revert(); return; }

    const ymd = format(parsed, 'yyyy-MM-dd');
    // Start must be strictly before end (matches schema's `end > start`).
    if (endValue && ymd >= endValue) { revert(); return; }

    if (ymd !== startValue) onChange({ start: ymd, end: endValue });
  };

  const commitEnd = (text: string) => {
    const revert = () => setEndText(formatForDisplay(endValue));

    if (!text.trim()) {
      if (endValue !== '') onChange({ start: startValue, end: '' });
      return;
    }
    const parsed = parseStrictDate(text);
    if (!parsed) { revert(); return; }
    if (minDate && parsed < minDate) { revert(); return; }

    const ymd = format(parsed, 'yyyy-MM-dd');
    if (startValue && ymd <= startValue) { revert(); return; }

    if (ymd !== endValue) onChange({ start: startValue, end: ymd });
  };

  const hasPresets = (startPresets?.length || endPresets?.length) ? true : false;

  const maskConfig = React.useMemo(() => buildDateMaskConfig(), []);

  // Wrapper that gives each masked input an underline + caret styling
  // matching the station accent. The underline is layered via an
  // absolutely-positioned span beneath the IMaskInput so we don't fight
  // browser native input borders.
  const renderMaskedInput = (
    inputId: string,
    ariaLabel: string,
    value: string,
    setValue: (next: string) => void,
    setFocused: (focused: boolean) => void,
    commit: (text: string) => void,
    focused: boolean,
    showError: boolean,
  ) => {
    const underlineColor = showError ? 'rgb(225, 29, 72)' : accentColor;
    return (
      <span className="relative inline-block">
        <IMaskInput
          {...maskConfig}
          id={inputId}
          aria-label={ariaLabel}
          value={value}
          unmask={false}
          onAccept={(next: unknown) => setValue(typeof next === 'string' ? next : '')}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(true);
            setOpen(true);
            try { e.currentTarget.select(); } catch { /* noop */ }
          }}
          onBlur={() => {
            commit(value);
            setFocused(false);
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
          placeholder="MM/DD/YYYY"
          className="bg-transparent border-0 px-0 pt-0 pb-0.5 text-[15px] tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none w-[110px] tabular-nums"
          style={{ caretColor: accentColor }}
        />
        {/* Underline — full input width, accent on focus, rose on
            error. Always rendered, opacity drives visibility so the
            transition stays smooth. */}
        <span
          aria-hidden
          className="pointer-events-none absolute left-0 right-0 bottom-0 h-[2px] rounded-full transition-opacity duration-150"
          style={{
            backgroundColor: underlineColor,
            opacity: focused || showError ? 1 : 0,
          }}
        />
      </span>
    );
  };

  return (
    <div
      className="relative"
      style={{ ['--accent' as string]: accentColor }}
    >
      <FieldCard accentColor={accentColor}>
        <div
          ref={triggerRowRef}
          className="relative px-5 pt-2 transition-colors focus-within:bg-[color:var(--accent-tint)] hover:bg-[color:var(--accent-tint)] group cursor-text"
          onClick={() => setOpen(true)}
        >
          <span
            aria-hidden
            className={
              error
                ? 'absolute left-0 top-3 bottom-3 w-[2px] rounded-r bg-rose-600 opacity-100 transition-opacity'
                : 'absolute left-0 top-3 bottom-3 w-[2px] rounded-r bg-[color:var(--accent)] opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 transition-opacity'
            }
          />
          <label
            htmlFor={`${id}-start`}
            className={`absolute left-5 top-3 pointer-events-none select-none text-[11px] font-medium tracking-[0.08em] uppercase ${
              error ? 'text-rose-600' : 'text-[color:var(--accent)]'
            }`}
          >
            {label}
            {required && (
              <span className={`ml-1 ${error ? 'text-rose-600' : 'text-[color:var(--accent)]'}`} aria-hidden>•</span>
            )}
          </label>

          {/* Two-column layout: START heading + input + pills on the left,
              END heading + input + duration badge + pills on the right,
              with the arrow anchored between the two input columns. The
              headings row above the inputs and the PresetGroups row below
              (rendered with showHeadings=false) share the same flex-1 /
              flex-1 split so the pills sit directly under their own input. */}
          <div className="w-full pt-7 pb-3">
            <div className="flex items-end gap-3">
              <div className="flex-1 min-w-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                Start
              </div>
              <div className="w-3.5 shrink-0" aria-hidden />
              <div className="flex-1 min-w-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                End
              </div>
              <div className="w-6 shrink-0" aria-hidden />
            </div>
            <div className="mt-1.5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                {renderMaskedInput(
                  `${id}-start`,
                  'Start date',
                  startText,
                  setStartText,
                  setStartFocused,
                  commitStart,
                  startFocused,
                  !!error,
                )}
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0 flex items-center gap-2">
                {renderMaskedInput(
                  `${id}-end`,
                  'End date',
                  endText,
                  setEndText,
                  setEndFocused,
                  commitEnd,
                  endFocused,
                  !!error,
                )}
                {durationDays != null && durationDays > 0 && (
                  <span
                    className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0"
                    style={{
                      backgroundColor: tintFromAccent(accentColor, 0.12),
                      color: accentColor,
                    }}
                  >
                    {durationDays} {durationDays === 1 ? 'day' : 'days'}
                  </span>
                )}
              </div>
              <button
                type="button"
                aria-label="Open date picker"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen((v) => !v);
                }}
                className="bg-transparent border-0 p-1 -m-1 rounded-md cursor-pointer focus:outline-none shrink-0"
              >
                <CalendarIcon
                  className="w-4 h-4 transition-colors"
                  style={{ color: open ? accentColor : 'rgb(148, 163, 184)' }}
                />
              </button>
            </div>
          </div>

          <FieldError message={error} className="mb-2" />
        </div>

        {hasPresets && (
          <div className="px-5 pt-3 pb-4">
            {/* Mirrors the trigger row's 4-cell layout so each pills column
                sits directly under its corresponding input column. */}
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0 flex flex-wrap gap-1.5 min-h-[28px]">
                {(startPresets ?? []).map((p) => (
                  <PresetChip
                    key={p.label}
                    preset={p}
                    accentColor={accentColor}
                    onApplied={applyStartPreset}
                  />
                ))}
              </div>
              <div className="w-3.5 shrink-0" aria-hidden />
              <div className="flex-1 min-w-0 flex flex-wrap gap-1.5 min-h-[28px]">
                {(endPresets ?? []).map((p) => (
                  <PresetChip
                    key={p.label}
                    preset={p}
                    accentColor={accentColor}
                    onApplied={applyEndPreset}
                  />
                ))}
              </div>
              <div className="w-6 shrink-0" aria-hidden />
            </div>
          </div>
        )}
      </FieldCard>

      <DateRangePopover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRowRef}
        startValue={startValue}
        endValue={endValue}
        onChange={onChange}
        min={min}
        minDisabledMessage={minDisabledMessage}
        startPresets={startPresets}
        endPresets={endPresets}
        accentColor={accentColor}
        primaryColor={primaryColor}
      />
    </div>
  );
};

export default DateRangeField;
